import { LoadingButton, LoadingButtonProps } from '@mui/lab'
import { createOrUpdateRuuviTag } from '../../services/ruuviTagService'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useState } from 'react'
import { RuuviTag } from '../../types'
import {
  createErrorNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'
import axios from 'axios'
import { usePolicy } from '../../hooks'

export interface RuuviTagCreateButtonProps extends Omit<
  LoadingButtonProps,
  'loading' | 'onClick' | 'children'
> {
  macAddress: string
  afterCreate?: (RuuviTag: RuuviTag) => void
}

export default function RuuviTagCreateButton({
  macAddress,
  afterCreate,
  ...props
}: RuuviTagCreateButtonProps) {
  const { t } = useTranslation('installation')

  const dispatch = useDispatch()

  const { canCreateRuuviTag } = usePolicy()

  const [isSubmitting, setSubmitting] = useState(false)

  async function handleSubmit() {
    try {
      setSubmitting(true)

      const controller = new AbortController()

      const created = await createOrUpdateRuuviTag(
        {
          id: macAddress
        },
        {
          signal: controller.signal
        }
      )

      dispatch(createSuccessNotification(t('createRuuviTagSuccess')))

      afterCreate?.(created)
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      dispatch(createErrorNotification(t('createRuuviTagError')))
    } finally {
      setSubmitting(false)
    }
  }

  if (!canCreateRuuviTag()) {
    return null
  }

  return (
    <LoadingButton
      variant="contained"
      color="primary"
      size="small"
      loading={isSubmitting}
      {...props}
      onClick={handleSubmit}
    >
      {t('ruuviTagCreatePromptConfirm')}
    </LoadingButton>
  )
}
